import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState, memo } from 'react'
import type { RecoveryScenario } from './api'

interface ScenarioActionsProps {
    scenario: RecoveryScenario
    onEdit: (scenario: RecoveryScenario) => void
}

const ScenarioActions = memo(function ScenarioActions({ scenario, onEdit }: ScenarioActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('recoveryScenarios.openMenu', 'Open menu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(scenario)
                            setOpen(false)
                        }}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t('recoveryScenarios.editScenario', 'Edit Scenario')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
})

export function createScenarioColumns(
    onEdit: (scenario: RecoveryScenario) => void,
    t: (key: string) => string
): ColumnDef<RecoveryScenario>[] {
    return [
        {
            accessorKey: 'name',
            header: t('recoveryScenarios.name'),
            size: 220,
            minSize: 160,
            maxSize: 320,
        },
        {
            accessorKey: 'description',
            header: t('recoveryScenarios.description'),
            size: 360,
            minSize: 220,
            maxSize: 560,
            cell: ({ row }) => {
                const description = row.getValue('description') as string | null
                return (
                    <div className="max-w-[360px] truncate text-sm" title={description || undefined}>
                        {description || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'useCase',
            header: t('recoveryScenarios.useCase'),
            size: 320,
            minSize: 200,
            maxSize: 500,
            cell: ({ row }) => {
                const useCase = row.getValue('useCase') as string | null
                return (
                    <div className="max-w-[320px] truncate text-sm" title={useCase || undefined}>
                        {useCase || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'isDefault',
            header: t('recoveryScenarios.default'),
            size: 120,
            minSize: 100,
            maxSize: 160,
            cell: ({ row }) => {
                const isDefault = row.getValue('isDefault') as boolean
                return isDefault ? (
                    <Badge variant="success">{t('recoveryScenarios.defaultBadge')}</Badge>
                ) : null
            },
        },
        {
            id: 'actions',
            size: 80,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const scenario = row.original
                return <ScenarioActions scenario={scenario} onEdit={onEdit} />
            },
        },
    ]
}
